/**
 * Shop deep-link builder + click tracker for Myntra, Ajio, and Amazon Fashion.
 *
 * The links stay vanilla by default. When admin sets `affiliateMode: true` with a
 * wrapper prefix (EarnKaro / Cuelinks / etc.), EVERY link becomes a commission link
 * across all three sites — with ZERO code changes.
 *
 * All URLs are built from the FULL descriptive term (color + type + style + gender)
 * so the buyer lands on a filtered search page, not a broad category.
 */

const MYNTRA_BASE = "https://www.myntra.com";
const AJIO_BASE = "https://www.ajio.com";
const AMAZON_BASE = "https://www.amazon.in/s";

/**
 * Compose the full, descriptive search term.
 * Priority order: color · style-qualifier · item-type · gender.
 * We always include gender (women/men) unless already in the string.
 *
 * @param {object} gap  - {category, keywords, color, fabric, name}
 * @param {string} gender - "women" | "men" | undefined
 * @returns {string[]} tokens in canonical order, deduped, lowercased.
 */
export function composeSearchTokens(gap, gender) {
  const raw = [
    gap.color,
    gap.fabric,
    gap.keywords || gap.name || gap.category,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  // split, keep alphanumerics + hyphens
  const tokens = raw
    .split(/[^a-z0-9]+/)
    .map((t) => t.trim())
    .filter(Boolean);

  // ensure gender token present exactly once at the end
  const genderWord = (gender || "").toLowerCase();
  const hasGender = ["women", "men", "girls", "boys"].some((g) => tokens.includes(g));
  if (!hasGender && genderWord) tokens.push(genderWord);

  // dedupe preserving order
  const seen = new Set();
  return tokens.filter((t) => (seen.has(t) ? false : (seen.add(t), true)));
}

function toSlug(tokens) {
  return tokens.join("-");
}
function toPhrase(tokens) {
  return tokens.join(" ");
}

/** Myntra: https://www.myntra.com/<slug>?rawQuery=<phrase> */
export function buildMyntraUrl(gap, gender) {
  const tokens = composeSearchTokens(gap, gender);
  if (tokens.length === 0) return `${MYNTRA_BASE}`;
  return `${MYNTRA_BASE}/${toSlug(tokens)}?rawQuery=${encodeURIComponent(toPhrase(tokens))}`;
}

/** Ajio: https://www.ajio.com/search/?text=<phrase> */
export function buildAjioUrl(gap, gender) {
  const tokens = composeSearchTokens(gap, gender);
  return `${AJIO_BASE}/search/?text=${encodeURIComponent(toPhrase(tokens))}`;
}

/** Amazon Fashion: https://www.amazon.in/s?k=<phrase>&i=fashion */
export function buildAmazonUrl(gap, gender) {
  const tokens = composeSearchTokens(gap, gender);
  return `${AMAZON_BASE}?k=${encodeURIComponent(toPhrase(tokens))}&i=fashion`;
}

/**
 * Wrap a raw shop URL through the admin-configured affiliate prefix.
 * `config` shape: { affiliateMode: boolean, affiliatePrefix: string }
 */
export function affiliateWrap(rawUrl, config) {
  if (!config || !config.affiliateMode || !config.affiliatePrefix) return rawUrl;
  return `${config.affiliatePrefix}${encodeURIComponent(rawUrl)}`;
}

/**
 * One helper returns the three affiliate-aware URLs for a gap card.
 */
export function buildShopUrls(gap, gender, config) {
  return {
    myntra: affiliateWrap(buildMyntraUrl(gap, gender), config),
    ajio: affiliateWrap(buildAjioUrl(gap, gender), config),
    amazon: affiliateWrap(buildAmazonUrl(gap, gender), config),
  };
}

/**
 * Legacy single-URL helper — kept for any older callers. Points to Myntra.
 */
export function buildShopUrl(gap, gender, config) {
  return affiliateWrap(buildMyntraUrl(gap, gender), config);
}

// ---- Local click counter (per-user, per-browser) ----
const COUNTER_KEY = "aynora.shop_clicks_v1";

export function trackShopClick(gap, site = "myntra") {
  try {
    const raw = localStorage.getItem(COUNTER_KEY);
    const stats = raw ? JSON.parse(raw) : { total: 0, by_category: {}, by_site: {} };
    stats.total = (stats.total || 0) + 1;
    const cat = gap?.category || "unknown";
    stats.by_category[cat] = (stats.by_category[cat] || 0) + 1;
    stats.by_site = stats.by_site || {};
    stats.by_site[site] = (stats.by_site[site] || 0) + 1;
    stats.last_click_at = new Date().toISOString();
    localStorage.setItem(COUNTER_KEY, JSON.stringify(stats));
    return stats;
  } catch {
    return null;
  }
}

export function getShopClickStats() {
  try {
    const raw = localStorage.getItem(COUNTER_KEY);
    return raw ? JSON.parse(raw) : { total: 0, by_category: {}, by_site: {} };
  } catch {
    return { total: 0, by_category: {}, by_site: {} };
  }
}
